/**
 * Persist Reporting composer across full page refresh (per organization + signed-in staff).
 */

import {
  createInitialDescriptionBlocks,
  ReportDescriptionBlock,
  createPictureBlock,
} from './reportDescriptionBlocks';
import {
  createDefaultReportingComposer,
  PreparedReportDraft,
  ReportingComposerState,
} from './reportingComposerState';
import { sanitizeReportSelectedActivity } from './reportSelectedActivity';

const STORAGE_PREFIX = 'lodgefirst_reporting_composer_v2';

/** Staff id, else username — empty when signed out so we never use a shared key. */
export const reportingComposerUserKey = (): string => {
  if (typeof localStorage === 'undefined') return '';
  return String(
    localStorage.getItem('staffId')
    || localStorage.getItem('username')
    || '',
  ).trim();
};

const storageKey = (): string | null => {
  const userKey = reportingComposerUserKey();
  if (!userKey) return null;
  const organizationId = (typeof localStorage !== 'undefined'
    && localStorage.getItem('organization_data_id'))
    || 'default';
  return `${STORAGE_PREFIX}:${organizationId}:${userKey}`;
};

const dropLegacyComposers = (): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    const prefixes = ['lodgefirst_reporting_composer_v1', STORAGE_PREFIX];
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (prefixes.some((p) => key.startsWith(`${p}:`))) {
        const rest = key.slice(key.indexOf(':') + 1);
        if (!rest.includes(':')) toRemove.push(key);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

const sanitizeDescBlocks = (raw: unknown): ReportDescriptionBlock[] => {
  if (!Array.isArray(raw) || !raw.length) return createInitialDescriptionBlocks();
  const out: ReportDescriptionBlock[] = [];
  raw.forEach((item: any) => {
    if (!item || typeof item !== 'object') return;
    if (item.kind === 'text' && typeof item.id === 'string') {
      out.push({ id: item.id, kind: 'text', text: String(item.text || '') });
      return;
    }
    if (item.kind === 'picture' && typeof item.id === 'string' && item.imageId) {
      out.push(createPictureBlock(String(item.imageId), String(item.fileName || 'image')));
      return;
    }
    if (item.kind === 'log' && typeof item.id === 'string' && item.sourceId) {
      out.push({
        id: item.id,
        kind: 'log',
        sourceId: String(item.sourceId),
        tmpId: Number(item.tmpId) > 0 ? Math.floor(Number(item.tmpId)) : 1,
        message: String(item.message || ''),
        type: String(item.type || 'Activity'),
        date: item.date ? String(item.date) : undefined,
        value: item.value != null ? String(item.value) : undefined,
        units: item.units != null ? String(item.units) : undefined,
        valueMode: item.valueMode === 'units' ? 'units' : undefined,
        combinedSourceIds: Array.isArray(item.combinedSourceIds)
          ? item.combinedSourceIds.map((id: unknown) => String(id || '').trim()).filter(Boolean)
          : undefined,
      });
    }
  });
  return out.length ? out : createInitialDescriptionBlocks();
};

const sanitizeDraft = (raw: unknown): PreparedReportDraft | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as any;
  if (!d.id) return null;
  return {
    id: String(d.id),
    viewUrl: d.viewUrl != null ? String(d.viewUrl) : null,
    preview: d.preview != null ? String(d.preview) : undefined,
  };
};

const sanitizeScopeChecked = (raw: unknown): string[] => (
  Array.isArray(raw)
    ? raw.map((v) => String(v || '').trim()).filter(Boolean)
    : []
);

/** Normalize a parsed blob into a safe composer snapshot. */
export const sanitizeReportingComposer = (raw: unknown): ReportingComposerState => {
  const base = createDefaultReportingComposer();
  if (!raw || typeof raw !== 'object') return base;
  const c = raw as any;
  return {
    toEmail: typeof c.toEmail === 'string' ? c.toEmail : '',
    subject: typeof c.subject === 'string' ? c.subject.slice(0, 120) : '',
    descBlocks: sanitizeDescBlocks(c.descBlocks),
    activeTextBlockId: typeof c.activeTextBlockId === 'string' ? c.activeTextBlockId : '',
    draft: sanitizeDraft(c.draft),
    replyToReportId: typeof c.replyToReportId === 'string' && c.replyToReportId
      ? String(c.replyToReportId)
      : null,
    replyToFromName: typeof c.replyToFromName === 'string' ? String(c.replyToFromName) : '',
    composerPhase: c.composerPhase === 'body' ? 'body' : 'details',
    activityScopeChecked: sanitizeScopeChecked(c.activityScopeChecked),
    activityScopeKey: typeof c.activityScopeKey === 'string' ? c.activityScopeKey : '',
    selectedActivity: sanitizeReportSelectedActivity(c.selectedActivity),
  };
};

export const loadReportingComposer = (): ReportingComposerState => {
  try {
    dropLegacyComposers();
    if (typeof localStorage === 'undefined') return createDefaultReportingComposer();
    const key = storageKey();
    if (!key) return createDefaultReportingComposer();
    const raw = localStorage.getItem(key);
    if (!raw) return createDefaultReportingComposer();
    return sanitizeReportingComposer(JSON.parse(raw));
  } catch {
    return createDefaultReportingComposer();
  }
};

export const saveReportingComposer = (composer: ReportingComposerState): void => {
  try {
    dropLegacyComposers();
    if (typeof localStorage === 'undefined') return;
    const key = storageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(composer));
  } catch {
    // Quota / private mode — leave memory state.
  }
};

export const clearReportingComposerStorage = (): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = storageKey();
    if (!key) return;
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};
